import React, { useState } from 'react';
import { 
  ArrowPathIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  CalendarDaysIcon,
  EnvelopeIcon,
  PhoneIcon,
  ShoppingBagIcon,
  SwatchIcon,
  ChatBubbleLeftEllipsisIcon
} from '@heroicons/react/24/outline';
import { facebookPixel } from '../utils/facebookPixel';
import { CameraIcon } from 'lucide-react';

// CSS Animations
const customStyles = `
  @keyframes fade-in-up {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-fade-in-up {
    animation: fade-in-up 0.6s ease-out forwards;
  }
`;

interface ExchangeForm {
  orderNumber: string;
  dateOfPurchase: string;
  customerName: string;
  emailAddress: string;
  phoneNumber: string;
  productName: string;
  productSize: string;
  productColor: string;
  reasonForExchange: string;
  reasonDetails: string;
  replacementName: string;
  replacementSize: string;
  replacementColor: string;
  photo: File | null;
  additionalComments: string;
}

const Exchange: React.FC = () => {
  const [formData, setFormData] = useState<ExchangeForm>({
    orderNumber: '',
    dateOfPurchase: '',
    customerName: '',
    emailAddress: '',
    phoneNumber: '',
    productName: '',
    productSize: 'M',
    productColor: '',
    reasonForExchange: 'Wrong size',
    reasonDetails: '',
    replacementName: '',
    replacementSize: 'M',
    replacementColor: '',
    photo: null,
    additionalComments: '',
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({
      ...prev,
      photo: file,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('order_number', formData.orderNumber);
      formDataToSend.append('date_of_purchase', formData.dateOfPurchase);
      formDataToSend.append('customer_name', formData.customerName);
      formDataToSend.append('email_address', formData.emailAddress);
      formDataToSend.append('phone_number', formData.phoneNumber);
      formDataToSend.append('product_name', formData.productName);
      formDataToSend.append('product_size', formData.productSize);
      formDataToSend.append('product_color', formData.productColor);
      formDataToSend.append('reason_for_exchange', formData.reasonForExchange);
      formDataToSend.append('reason_details', formData.reasonDetails);
      formDataToSend.append('replacement_name', formData.replacementName);
      formDataToSend.append('replacement_size', formData.replacementSize);
      formDataToSend.append('replacement_color', formData.replacementColor);
      formDataToSend.append('additional_comments', formData.additionalComments);
      
      if (formData.photo) {
        formDataToSend.append('photo', formData.photo);
      }

      // Import apiService dynamically to avoid circular imports
      const { default: apiService } = await import('../services/api');
      
      const response = await apiService.submitExchangeRequest(formDataToSend);
      
      if (response.success) {
        // Track Facebook Pixel Lead event for exchange request
        facebookPixel.trackLead({
          content_name: 'Exchange Request Submission',
          value: 0,
          currency: 'MMK'
        });
        
        setSubmitted(true);
      } else {
        console.error('Failed to submit exchange request:', response.message);
        alert('Failed to submit exchange request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting exchange request:', error);
      alert('An error occurred while submitting your request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl p-12 text-center border border-green-100">
              <div className="w-24 h-24 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <CheckCircleIcon className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Exchange Request Submitted!
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Thank you for your exchange request. We will review your request and contact you within 24–48 hours.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="bg-gradient-to-r from-gray-800 to-gray-900 text-white px-8 py-4 rounded-xl font-semibold hover:from-gray-700 hover:to-gray-800 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Submit Another Request
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <style dangerouslySetInnerHTML={{ __html: customStyles }} />

      {/* Hero Section */}
      <div className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 animate-fade-in-up">
              Exchange Request
            </h1>
            <p
              className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed animate-fade-in-up"
              style={{ animationDelay: '0.2s' }}
            >
              Size မတော်တာလား? အရောင်မကြိုက်တာလား? Signature Styles မှာဆိုအလွယ်တကူပစ္စည်းပြန်လဲပေးတယ်နော်! 
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-8">
        {/* Instructions Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
          <div className="flex items-center mb-6">
            <ExclamationTriangleIcon className="w-8 h-8 text-gray-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Exchange Policy &amp; Guidelines</h2>
          </div>

          <div className="text-gray-700 leading-relaxed">
            <p className="mb-6 text-lg font-medium text-gray-900">
              ပစ္စည်းပြန်လဲလှည်မည်ဆိုပါက စည်းကမ်းချက်များကိုသေချာလေးဖတ်ရှုပေးပါရန်။
            </p>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CheckCircleIcon className="w-5 h-5 text-gray-600 mr-2" />
                စည်းကမ်းချက်များ:
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <ol className="list-decimal list-inside space-y-3 text-sm">
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-0.5">
                      1
                    </span>
                    <span>ပစ္စည်းလဲလှယ်ခြင်းကို ပစ္စည်းလက်ခံရရှိသည့် ရက်မှ စ၍ (၂) ရက်အတွင်း တင်ပြပေးရပါမည်။ ( ပစ္စည်းလက်ခံရရှိသည့် ရက်မှ စ၍ (၂ ) ရက်ထက်ကျော်လွန်သွားပါက ပစ္စည်းပြန်လည်လဲလှယ်ပေးမည် မဟုတ်ပါ)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-0.5">
                      2
                    </span>
                    <span>ပြန်လဲလိုသည့် ပစ္စည်းသည် Signature Styles မှ ပို့ထားသည့် အခြေနေ အတိုင်းဖြစ်နေရန်လိုအပ်ပါသည်။</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-0.5">
                      3
                    </span>
                    <span>Tags များဖြုတ်ထားခြင်း၊ customer များကြောင့် ပစ္စည်း damage ဖြစ်ခြင်း၊ အရောင်စွန်းနေခြင်း ၊ original packing အိတ်မပါခြင်းတို့အတွက် ပစ္စည်းပြန်လဲလှယ်ပေးမည်မဟုတ်ပါ။</span>
                  </li>
                </ol>
                <ol className="list-decimal list-inside space-y-3 text-sm" start={4}>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-0.5">
                      4
                    </span>
                    <span>ပစ္စည်းလဲခြင်းသည် size နှင့် color အပြောင်းလဲအတွက်သာ အကျုံးဝင်ပါသည်။</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-0.5">
                      5
                    </span>
                    <span>ပစ္စည်းလဲလှယ်ရာတွက် ကျသင့်သော ပို့ခ များကို  customer များဘက်မှ ကျခံပေးရပါမည်။</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center text-xs font-bold mr-3 mt-0.5">
                      6
                    </span>
                    <span>Signature Styles ဘက်မှ မှားထည့်ပေးလ်ိုက်သည့်ပစ္စည်းများအတွက် Signature Styles မှ ပို့ခကျခံ၍ ပစ္စည်းများပြန်လည် လဲလှယ်ပေးပါမည်။</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Order Information */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <ShoppingBagIcon className="w-7 h-7 text-gray-600 mr-3" />
                Order Information
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Order Number *
                  </label>
                  <input
                    type="text"
                    name="orderNumber"
                    value={formData.orderNumber}
                    onChange={handleChange}
                    placeholder="Enter your order number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-500 focus:shadow-md"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <CalendarDaysIcon className="w-4 h-4 mr-2" />
                    Date of Purchase *
                  </label>
                  <input
                    type="date"
                    name="dateOfPurchase"
                    value={formData.dateOfPurchase}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-500 focus:shadow-md"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <EnvelopeIcon className="w-7 h-7 text-gray-600 mr-3" />
                Customer Information
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-500 focus:shadow-md"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <EnvelopeIcon className="w-4 h-4 mr-2" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="emailAddress"
                    value={formData.emailAddress}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-500 focus:shadow-md"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <PhoneIcon className="w-4 h-4 mr-2" />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="+95 9XXXXXXXXX"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-500 focus:shadow-md"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Product to Exchange */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <SwatchIcon className="w-7 h-7 text-gray-600 mr-3" />
                Product Information
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Item to Exchange *
                  </label>
                  <input
                    type="text"
                    name="productName"
                    value={formData.productName}
                    onChange={handleChange}
                    placeholder="Enter product name or code"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-500 focus:shadow-md"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Size *
                  </label>
                  <select
                    name="productSize"
                    value={formData.productSize}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-500 focus:shadow-md cursor-pointer"
                    required
                  >
                    <option value="XS">XS</option>
                    <option value="S">S</option>
                    <option value="M">M</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                    <option value="2XL">2XL</option>
                    <option value="3XL">3XL</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Color *
                  </label>
                  <input
                    type="text"
                    name="productColor"
                    value={formData.productColor}
                    onChange={handleChange}
                    placeholder="e.g., Black, White, Red"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-500 focus:shadow-md"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Reason for Exchange */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <ExclamationTriangleIcon className="w-7 h-7 text-gray-600 mr-3" />
                Reason for Exchange
              </h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Reason *
                  </label>
                  <select
                    name="reasonForExchange"
                    value={formData.reasonForExchange}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white transition-all duration-200 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-500 focus:shadow-md cursor-pointer"
                    required
                  >
                    <option value="Wrong size">Wrong size</option>
                    <option value="Color preference">Color preference</option>
                    <option value="Product defect">Product defect (attach photo)</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                {formData.reasonForExchange === 'Other' && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Please specify
                    </label>
                    <textarea
                      name="reasonDetails"
                      value={formData.reasonDetails}
                      onChange={handleChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-500 focus:shadow-md resize-vertical"
                      placeholder="Please provide more details about your reason for exchange..."
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Replacement Request */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <ArrowPathIcon className="w-7 h-7 text-gray-600 mr-3" />
                Replacement Request
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    name="replacementName"
                    value={formData.replacementName}
                    onChange={handleChange}
                    placeholder="Enter replacement product name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-500 focus:shadow-md"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Size *
                  </label>
                  <select
                    name="replacementSize"
                    value={formData.replacementSize}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-500 focus:shadow-md cursor-pointer"
                    required
                  >
                    <option value="XS">XS</option>
                    <option value="S">S</option>
                    <option value="M">M</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                    <option value="2XL">2XL</option>
                    <option value="3XL">3XL</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Color *
                  </label>
                  <input
                    type="text"
                    name="replacementColor"
                    value={formData.replacementColor}
                    onChange={handleChange}
                    placeholder="e.g., Black, White, Red"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-500 focus:shadow-md"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Upload Picture */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <CameraIcon className="w-7 h-7 text-gray-600 mr-3" />
                Upload Picture (Optional)
              </h3>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Product Photo (For quality verification)
                </label>
                
                <div className="relative">
                  <input
                    type="file"
                    id="photo-upload"
                    accept="image/jpeg,image/png,image/jpg"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="photo-upload"
                    className="flex flex-col items-center justify-center w-full h-32 px-4 transition bg-white border-2 border-gray-300 border-dashed rounded-lg appearance-none cursor-pointer hover:border-gray-400 hover:bg-gray-50 focus:outline-none"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <CameraIcon className="w-10 h-10 mb-3 text-gray-400" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">JPG or PNG (MAX. 5MB)</p>
                    </div>
                  </label>
                </div>
                
                {formData.photo && (
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm font-medium text-green-800">
                          {formData.photo.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, photo: null }))}
                        className="text-green-600 hover:text-green-800 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <p className="text-xs text-green-600 mt-1">
                      {(formData.photo.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Comments */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <ChatBubbleLeftEllipsisIcon className="w-7 h-7 text-gray-600 mr-3" />
                Additional Information
              </h3>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Additional Comments
                </label>
                <textarea
                  name="additionalComments"
                  value={formData.additionalComments}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-500 focus:shadow-md resize-vertical"
                  placeholder="Please provide any additional information about your exchange request, special instructions, or concerns..."
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center pt-8">
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 rounded-lg bg-gray-800 text-white hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-6 w-6 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing Request...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <ArrowPathIcon className="w-6 h-6 mr-3" />
                    SUBMIT EXCHANGE REQUEST
                  </div>
                )}
              </button>
              <p className="text-sm text-gray-500 mt-4 max-w-2xl mx-auto">
                By submitting this form, you agree to our exchange policy and terms. We will review your request and contact you within 24–48 hours.
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Exchange;